import { LoadingSpinner } from '../components/LoadingSpinner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import type { ProfileUpdateError, ProfileUpdateRequest } from '../services/api';

type ProfileLike = {
    given_name?: string;
    family_name?: string;
    name?: string;
    email?: string;
    picture?: string;
    email_verified?: boolean;
    created_at: string;
    updated_at: string;
};

function RestrictedFieldHint({ restricted }: { restricted: boolean }) {
    if (!restricted) {
        return null;
    }

    return <span className="text-xs text-amber-600 ml-2">(Managed by your identity provider)</span>;
}

function ProfileInputField({
    id,
    label,
    type,
    value,
    placeholder,
    restricted,
    onChange,
}: {
    id: keyof ProfileUpdateRequest;
    label: string;
    type: string;
    value: string;
    placeholder: string;
    restricted: boolean;
    onChange: (field: keyof ProfileUpdateRequest, value: string) => void;
}) {
    return (
        <div>
            <label htmlFor={id} className="block text-sm font-medium text-neutral-700 mb-2">
                {label}
                <RestrictedFieldHint restricted={restricted} />
            </label>
            <Input
                id={id}
                type={type}
                value={value}
                onChange={(event) => onChange(id, event.target.value)}
                placeholder={placeholder}
                className="w-full"
                disabled={restricted}
            />
        </div>
    );
}

function ProfileDisplayField({
    label,
    value,
    restricted,
}: {
    label: string;
    value: string;
    restricted?: boolean;
}) {
    return (
        <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
                {label}
                <RestrictedFieldHint restricted={Boolean(restricted)} />
            </label>
            <p className={`text-sm ${restricted ? 'text-amber-700' : 'text-neutral-900'}`}>
                {value}
            </p>
        </div>
    );
}

function ProfileUpdateErrorMessage({ error }: { error: Error }) {
    try {
        const errorData = JSON.parse(error.message) as ProfileUpdateError;
        if (errorData.restrictedFields) {
            return (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <div className="text-amber-800 font-medium">{errorData.error}</div>
                    {errorData.details ? (
                        <div className="text-amber-700 text-xs mt-1">{errorData.details}</div>
                    ) : null}
                </div>
            );
        }
    } catch {
        // Fall back to the generic error block below.
    }

    return <div className="text-red-600">Error updating profile: {error.message}</div>;
}

export function ProfileEditForm({
    formData,
    restrictedFields,
    isPending,
    error,
    onInputChange,
    onSubmit,
    onCancel,
}: {
    formData: ProfileUpdateRequest;
    restrictedFields: string[];
    isPending: boolean;
    error: Error | null;
    onInputChange: (field: keyof ProfileUpdateRequest, value: string) => void;
    onSubmit: (event: React.FormEvent) => void;
    onCancel: () => void;
}) {
    const isFieldRestricted = (fieldName: string) => restrictedFields.includes(fieldName);

    return (
        <form onSubmit={onSubmit} className="space-y-6">
            {restrictedFields.length > 0 ? (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg
                                className="h-5 w-5 text-amber-400"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                            >
                                <path
                                    fillRule="evenodd"
                                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                    clipRule="evenodd"
                                />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-amber-800">
                                Some fields are managed by your identity provider
                            </h3>
                            <div className="mt-2 text-sm text-amber-700">
                                <p>
                                    Fields like name and email are controlled by your Google/SAML
                                    account and cannot be changed here. You can only edit fields
                                    that are not grayed out or marked as restricted.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <ProfileInputField
                    id="given_name"
                    label="First Name"
                    type="text"
                    value={formData.given_name || ''}
                    placeholder="Enter your first name"
                    restricted={isFieldRestricted('given_name')}
                    onChange={onInputChange}
                />
                <ProfileInputField
                    id="family_name"
                    label="Last Name"
                    type="text"
                    value={formData.family_name || ''}
                    placeholder="Enter your last name"
                    restricted={isFieldRestricted('family_name')}
                    onChange={onInputChange}
                />
            </div>

            <ProfileInputField
                id="name"
                label="Full Name"
                type="text"
                value={formData.name || ''}
                placeholder="Enter your full name"
                restricted={isFieldRestricted('name')}
                onChange={onInputChange}
            />
            <ProfileInputField
                id="email"
                label="Email Address"
                type="email"
                value={formData.email || ''}
                placeholder="Enter your email address"
                restricted={isFieldRestricted('email')}
                onChange={onInputChange}
            />
            <ProfileInputField
                id="picture"
                label="Profile Picture URL"
                type="url"
                value={formData.picture || ''}
                placeholder="https://example.com/avatar.jpg"
                restricted={isFieldRestricted('picture')}
                onChange={onInputChange}
            />

            <div className="flex items-center justify-end space-x-3 pt-6 border-t border-neutral-200">
                <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
                    Cancel
                </Button>
                <Button type="submit" disabled={isPending} className="min-w-24">
                    {isPending ? <LoadingSpinner className="w-4 h-4" /> : 'Save Changes'}
                </Button>
            </div>

            {error ? (
                <div className="text-sm mt-2">
                    <ProfileUpdateErrorMessage error={error} />
                </div>
            ) : null}
        </form>
    );
}

export function ProfileReadOnlyView({
    profile,
    restrictedFields,
}: {
    profile: ProfileLike;
    restrictedFields: string[];
}) {
    const isFieldRestricted = (fieldName: string) => restrictedFields.includes(fieldName);
    const profileInitial =
        profile.name?.charAt(0)?.toUpperCase() || profile.email?.charAt(0)?.toUpperCase() || '?';

    return (
        <div className="space-y-6">
            <div className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                    {profile.picture ? (
                        <img
                            src={profile.picture}
                            alt="Profile"
                            className="w-16 h-16 rounded-full object-cover"
                        />
                    ) : (
                        <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center">
                            <span className="text-primary-600 font-semibold text-lg">
                                {profileInitial}
                            </span>
                        </div>
                    )}
                </div>
                <div>
                    <h3 className="text-lg font-semibold text-neutral-900">
                        {profile.name || 'No name set'}
                    </h3>
                    <p className="text-sm text-neutral-600">{profile.email}</p>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <ProfileDisplayField
                    label="First Name"
                    value={profile.given_name || 'Not set'}
                    restricted={isFieldRestricted('given_name')}
                />
                <ProfileDisplayField
                    label="Last Name"
                    value={profile.family_name || 'Not set'}
                    restricted={isFieldRestricted('family_name')}
                />
                <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                        Email Verified
                    </label>
                    <div className="flex items-center space-x-2">
                        <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                profile.email_verified
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-yellow-100 text-yellow-800'
                            }`}
                        >
                            {profile.email_verified ? 'Verified' : 'Not Verified'}
                        </span>
                    </div>
                </div>
                <ProfileDisplayField
                    label="Created"
                    value={new Date(profile.created_at).toLocaleDateString()}
                />
                <ProfileDisplayField
                    label="Last Updated"
                    value={new Date(profile.updated_at).toLocaleDateString()}
                />
            </div>
        </div>
    );
}
